import { ArrowRight, ArrowUpRight, Github, Lock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ComplexityMeter } from '@/components/ui/Complexity';
import { TechChipRow } from '@/components/ui/TechChip';
import { cx, formatDateRange } from '@/lib/format';
import { localized, type Locale, type Project } from '@/lib/types';
import { useI18n } from '@/store/preferences';

/**
 * A card is a data plate, not a screenshot: the head carries the state, the
 * body carries the load reading and the counts that justify it.
 */
export function ProjectCard({ project }: { project: Project }) {
  const { t, locale } = useI18n();
  const isLive = project.status === 'IN_PRODUCTION';

  const secondary = project.isRepoPrivate
    ? project.liveUrl
      ? { href: project.liveUrl, icon: ArrowUpRight, label: t.project.viewLive }
      : null
    : project.repoUrl
      ? { href: project.repoUrl, icon: Github, label: t.project.viewCode }
      : null;

  return (
    <article className="plate-interactive group flex h-full flex-col">
      <Link
        to={`/projects/${project.slug}`}
        className="absolute inset-0 z-10"
        aria-label={project.title}
      />

      <header className="plate-head">
        <span className="flex min-w-0 items-center gap-2 font-mono text-2xs uppercase tracking-[0.14em]">
          {isLive && <span className="h-1.5 w-1.5 shrink-0 bg-accent" aria-hidden />}
          <span className={cx('truncate', isLive ? 'text-accent' : 'text-faint')}>
            {t.status[project.status]}
          </span>
        </span>
        <span className="shrink-0 font-mono text-2xs tabular text-faint">
          {formatDateRange(project.startDate, project.endDate, locale, t.project.present)}
        </span>
      </header>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h3 className="card-title text-balance transition-colors duration-200 group-hover:text-accent">
          {project.title}
        </h3>

        <p className="mt-3 text-pretty text-sm leading-relaxed text-muted">
          {localized(project, 'summary', locale)}
        </p>

        {project.authorship && (
          <p className="mt-4 inline-flex w-fit items-center gap-1.5 border border-signal/35 bg-signal/10 px-2 py-1 font-mono text-2xs uppercase tracking-[0.12em] text-signal">
            <Sparkles className="h-3 w-3" aria-hidden />
            {t.authorship[project.authorship.mode]}
          </p>
        )}

        {project.complexity && (
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line pt-4">
            <ComplexityMeter complexity={project.complexity} />
            <span className="font-mono text-2xs uppercase tracking-[0.14em] text-faint">
              {t.complexity.title}
            </span>
          </div>
        )}

        {project.metrics && project.metrics.length > 0 && (
          <MetricLine project={project} locale={locale} />
        )}

        {project.isRepoPrivate && (
          <p className="mt-4 inline-flex w-fit items-center gap-1.5 font-mono text-2xs text-faint">
            <Lock className="h-3 w-3" aria-hidden />
            {t.project.privateRepo}
          </p>
        )}

        <div className="relative z-20 mt-5">
          <TechChipRow technologies={project.technologies} max={8} size="sm" />
        </div>

        <div className="flex-1" aria-hidden />
      </div>

      <footer className="flex items-center justify-between gap-4 border-t border-line px-5 py-3.5 sm:px-6">
        <span className="inline-flex items-center gap-1.5 font-mono text-2xs uppercase tracking-[0.12em] text-ink">
          {t.project.overview}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
        </span>

        {secondary && (
          <a
            href={secondary.href}
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-20 inline-flex items-center gap-1.5 font-mono text-2xs uppercase tracking-[0.12em] text-muted transition-colors hover:text-accent"
            onClick={(event) => event.stopPropagation()}
          >
            <secondary.icon className="h-3.5 w-3.5" aria-hidden />
            {secondary.label}
          </a>
        )}
      </footer>
    </article>
  );
}

/** The counts that back the load reading, on one line and in the data face. */
function MetricLine({ project, locale }: { project: Project; locale: Locale }) {
  return (
    <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-2xs text-faint">
      {(project.metrics ?? []).map((metric) => (
        <div key={metric.en} className="inline-flex items-baseline gap-1.5">
          <dd className="tabular text-ink">{metric.value}</dd>
          <dt>{locale === 'es' ? metric.es : metric.en}</dt>
        </div>
      ))}
    </dl>
  );
}

/** Compact row for coursework, so small projects never outshout real work. */
export function MinorProjectRow({ project }: { project: Project }) {
  const { t, locale } = useI18n();

  return (
    <article className="group relative flex items-center gap-4 border-b border-line px-4 py-4 transition-colors last:border-0 hover:bg-elevated sm:px-5">
      <Link to={`/projects/${project.slug}`} className="absolute inset-0" aria-label={project.title} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-base font-semibold tracking-tight transition-colors group-hover:text-accent">
            {project.title}
          </h3>
          <span className="font-mono text-2xs tabular text-faint">
            {new Date(project.startDate).getUTCFullYear()}
          </span>
        </div>
        <p className="mt-1 truncate text-sm text-muted">{localized(project, 'summary', locale)}</p>
      </div>

      <ul className="hidden shrink-0 gap-1.5 sm:flex">
        {project.technologies.slice(0, 2).map((tech) => (
          <li key={tech.id} className="chip">
            {tech.name}
          </li>
        ))}
      </ul>

      {project.repoUrl && !project.isRepoPrivate && (
        <a
          href={project.repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-icon relative z-10 shrink-0"
          aria-label={`${t.project.viewCode}: ${project.title}`}
        >
          <Github className="h-4 w-4" />
        </a>
      )}
    </article>
  );
}
